// src/components/padrekko/qc/QcCaseBar.jsx
//
// The PadRekko case bar: everything CASE-scoped about the open QC case, in a
// bar above the map. The inspector panel keeps everything pad- and
// finding-scoped. The split rule is scope, not actionability:
//
//   BAR    case identity, customer, stage, deadline, progress through pads,
//          case-level counts, participants, and the stage-transition actions.
//          Context plus stage transitions; no navigation.
//   PANEL  pad header, geometry-resolution banner, pad counts, the polygon
//          drawer, findings, attachments, pad nav, "Back to summary", and
//          "‹ Cases" in the panel header (QcBackToCases). Both switch what
//          the panel shows, so both are panel navigation.
//
// IT PUSHES THE MAP DOWN; IT DOES NOT OVERLAY IT. MapPage mounts it above a
// wrapper holding the whole map area, so every top-anchored map control moves
// down with the map, and MapLibre resizes itself (trackResize's
// ResizeObserver). An overlay would risk covering the pad at the inspector's
// maxZoom 17 framing.
//
// IT RESERVES NOTHING WHEN THERE IS NO CASE: no session or no case renders
// null, so the map gets the full area it had before this bar existed.
//
// ── WIDTH TIERS ──────────────────────────────────────────────────────────
// This view defines no breakpoints, and the map absorbs all shrinkage, so the
// bar measures its own width and degrades in a fixed order. Tiers change with
// WIDTH ONLY, never with content, so the bar's height does not jump while
// someone is working:
//   full   ≥ 1250  everything
//   mid    ≥ 950   case counts leave the line; the progress title carries them
//   narrow ≥ 700   customer and deadline leave too; they stay as screen-reader text
//   tight  < 700   the role line leaves; the stage badge's title carries it
// NEVER HIDDEN at any width: the stage badge, every stage-transition action
// and the participants button (CaseBanner's [data-qc-stage-actions] group,
// which is shrink-0).
//
// Tab order follows the DOM: the left panel comes before the map section, so
// the pad work is reached before the stage actions. That was chosen, not
// inherited: Tab follows the work, and stage actions are once per case.

import { useLayoutEffect, useState } from "react";
import { useQcStore } from "../../../stores/useQcStore";
import CaseBanner from "./CaseBanner";
import { CaseCountsLine } from "./QcCountsSummary";
import { countCompletion } from "./completionCounts";

function tierForWidth(w) {
  if (!Number.isFinite(w) || w <= 0) return "full";
  if (w >= 1250) return "full";
  if (w >= 950) return "mid";
  if (w >= 700) return "narrow";
  return "tight";
}

function useWidthTier() {
  const [el, setEl] = useState(null);
  const [tier, setTier] = useState("full");
  useLayoutEffect(() => {
    if (!el) return undefined;
    const apply = (w) => setTier((t) => { const n = tierForWidth(w); return n === t ? t : n; });
    apply(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) apply(e.contentRect?.width ?? e.target.getBoundingClientRect().width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, tier];
}

function customerName(caseInfo) {
  const p = (caseInfo?.participants ?? []).find((x) => x.role === "customer_analyst");
  return p?.org_name ?? null;
}

export default function QcCaseBar() {
  const session       = useQcStore((s) => s.session);
  const caseInfo      = useQcStore((s) => s.caseInfo);
  const currentPadIdx = useQcStore((s) => s.currentPadIdx);
  const [ref, tier] = useWidthTier();

  if (!session || !caseInfo) return null;

  const pads = session.pads ?? [];
  const padCount = pads.length;
  const padIdx = currentPadIdx ?? 0;
  const progressPct = padCount === 0 ? 0 : ((padIdx + 1) / padCount) * 100;
  const allFindings = pads.flatMap((p) => p.findings ?? []);
  const caseReview = session.review_counts ?? null;
  const reviewStage = session.review_counts_stage ?? null;

  const showCounts    = tier === "full";
  const showDetails   = tier === "full" || tier === "mid";   // customer, deadline
  const hideRole      = tier === "tight";

  const customer = customerName(caseInfo);
  const deadline = caseInfo.response_deadline_date ?? null;
  const deadlineTitle = caseInfo.response_deadline_basis
    ? `Response deadline · ${caseInfo.response_deadline_basis}`
    : "Response deadline";

  // What leaves the line at a narrower tier stays reachable in a title.
  const comp = countCompletion(allFindings);
  const countsTitle = [
    comp.total > 0 ? `Case completion: ${comp.completed} completed · ${comp.not_completed} not completed · ${comp.pending} pending / ${comp.total}` : null,
    caseReview && (caseReview.active || caseReview.removed) ? `Case review: ${caseReview.reviewed}/${caseReview.active} reviewed` : null,
  ].filter(Boolean).join(" — ");

  const identityExtra = showDetails
    ? (customer && (
        <span className="text-[12px] text-fg-secondary truncate min-w-0" data-qc-customer title={`Customer: ${customer}`}>
          · {customer}
        </span>
      ))
    : (customer || deadline) && (
        <span className="sr-only" data-qc-details-hidden>
          {customer ? `Customer: ${customer}. ` : ""}{deadline ? `Response deadline ${deadline}.` : ""}
        </span>
      );

  const middle = (
    <div className="flex items-center gap-3 shrink-0">
      {showDetails && deadline && (
        <span className="text-[12px] text-slate-300 whitespace-nowrap" title={deadlineTitle} data-qc-deadline>
          <span className="text-fg-secondary">Due</span> {deadline}
        </span>
      )}
      <div
        className="flex items-center gap-1.5"
        title={showCounts || !countsTitle ? `Pad ${padIdx + 1} of ${padCount}` : `Pad ${padIdx + 1} of ${padCount} — ${countsTitle}`}
        data-qc-progress
      >
        <span className="text-[12px] text-slate-300 whitespace-nowrap tabular-nums">
          Pad {padIdx + 1} of {padCount}
        </span>
        <div
          className="h-1 w-16 rounded bg-slate-800/80 overflow-hidden"
          role="progressbar"
          aria-valuenow={padIdx + 1}
          aria-valuemin={0}
          aria-valuemax={padCount}
        >
          <div className="h-full bg-amber-400/70 transition-[width]" style={{ width: `${progressPct}%` }} />
        </div>
      </div>
      {showCounts && (
        <CaseCountsLine findings={allFindings} reviewCounts={caseReview} stage={reviewStage} />
      )}
    </div>
  );

  return (
    <div ref={ref} className="shrink-0 min-w-0" data-qc-case-bar data-qc-tier={tier}>
      <CaseBanner identityExtra={identityExtra} middle={middle} hideRole={hideRole} />
    </div>
  );
}
